// W-0531 — Solicita Reembolso Clientes (IPTU) — tela em lote
// Single-page com seções colapsáveis (ADR-003).
// Fluxo: Filtros → Buscar → Grid (multi-seleção + edição inline) → Parâmetros → Lançar
// LICOES aplicadas:
// - §1.5 key estável (codigo_boleto) ✔
// - §1.6 Yup completo (ParametrosLancamento) ✔
// - Ciclo 9 L3 cleanup `ativo` em useEffect ✔
// - Ciclo 9 L4 err.message com fallback ternário ✔
// - FE-006 AND explícito para chars 666 + 630 ✔

import React, { useState, useCallback } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import AuthStorage from '~/Utils/AuthStorage';
import {
  buscarImoveisReembolsoIptu,
  lancarReembolsoIptu,
  verificarChavesReembolsoIptu,
} from '~/Services/Service';
import {
  CARACTERISTICA_IMPLANTA_REEMBOLSAVEIS,
  CARACTERISTICA_REEMBOLSO_LIBERADO,
} from '~/constant';
import FiltrosBusca from './components/FiltrosBusca';
import PainelCalculo from './components/PainelCalculo';
import GridImoveis from './components/GridImoveis';
import ParametrosLancamento from './components/ParametrosLancamento';
import ModalImportarExcel from './components/ModalImportarExcel';
import ModalConfirmacaoChaves from './components/ModalConfirmacaoChaves';

const ReembolsoIptu = () => {
  const [imoveis, setImoveis] = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [calculo, setCalculo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [somenteLotesQuitados, setSomenteLotesQuitados] = useState(false);
  const [filtroAtual, setFiltroAtual] = useState(null);
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  const [showImportarExcel, setShowImportarExcel] = useState(false);
  const [showParametros, setShowParametros] = useState(false);
  const [showConfirmacaoChaves, setShowConfirmacaoChaves] = useState(false);
  const [chavesPendentes, setChavesPendentes] = useState([]);
  const [payloadPendente, setPayloadPendente] = useState(null);

  const handleBuscar = useCallback((filtro) => {
    setLoading(true);
    setFiltroAtual(filtro);
    setSelecionados(new Set());
    setCalculo(null);
    setBuscaRealizada(false);

    buscarImoveisReembolsoIptu(filtro)
      .then((res) => {
        const imoveisRetornados = res.imoveis || [];
        setImoveis(imoveisRetornados);
        setCalculo(res.calculo || null);
        setBuscaRealizada(true);
        if (imoveisRetornados.length === 0) {
          toast.info('Nenhum imóvel encontrado para os filtros informados');
        }
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao buscar imóveis';
        toast.error(msg);
        setBuscaRealizada(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = (codigoBoleto) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(codigoBoleto)) novo.delete(codigoBoleto);
      else novo.add(codigoBoleto);
      return novo;
    });
  };

  const handleToggleTodos = (marcar) => {
    if (marcar) setSelecionados(new Set(imoveis.map((i) => i.codigo_boleto)));
    else setSelecionados(new Set());
  };

  const handleEditarParcelas = (codigoBoleto, valor) => {
    setImoveis((prev) =>
      prev.map((i) => (i.codigo_boleto === codigoBoleto ? { ...i, parcelas: valor } : i))
    );
  };

  const handleEditarValor = (codigoBoleto, valor) => {
    setImoveis((prev) =>
      prev.map((i) => (i.codigo_boleto === codigoBoleto ? { ...i, valor_parcela: valor } : i))
    );
  };

  const handleAplicarImportacao = (linhas) => {
    setImoveis((prev) =>
      prev.map((item) => {
        const importado = linhas.find((l) => l.codigo_ativo === item.codigo_ativo);
        if (!importado) return item;
        return {
          ...item,
          parcelas: importado.parcelas != null ? importado.parcelas : item.parcelas,
          valor_parcela: importado.valor_parcela != null ? importado.valor_parcela : item.valor_parcela,
        };
      })
    );
    toast.success('Dados importados aplicados ao grid');
  };

  const buildPayload = (parametros) => {
    const imoveisSelecionados = imoveis
      .filter((i) => selecionados.has(i.codigo_boleto))
      .map((i) => ({
        codigo_boleto: i.codigo_boleto,
        codigo_ativo: i.codigo_ativo,
        carteira: i.carteira,
        parcelas: i.parcelas,
        valor_total: i.valor_iptu_lote,
      }));

    return {
      codigo_familia: filtroAtual ? filtroAtual.codigo_familia : null,
      somente_lotes_quitados: somenteLotesQuitados,
      primeiro_vencimento: parametros.primeiroVencimento || null,
      codigo_reembolso: parametros.codigoReembolso,
      mensagem1: parametros.mensagem1 || null,
      mensagem2: parametros.mensagem2 || null,
      justificativa: parametros.justificativa || null,
      imoveis: imoveisSelecionados,
    };
  };

  const executarLancamento = (payload) => {
    setProcessando(true);
    lancarReembolsoIptu(payload)
      .then((res) => {
        const total = res.total_lancados != null ? res.total_lancados : 0;
        const erros = res.total_erros != null ? res.total_erros : 0;
        if (erros > 0) {
          toast.error(`Lançamento concluído com erro(s): ${total} lançado(s), ${erros} erro(s)`);
        } else {
          toast.success(`Lançamento concluído: ${total} lançado(s), ${erros} erro(s)`);
        }
        if (res.mensagens && res.mensagens.length) {
          res.mensagens.forEach((m) => toast.info(m));
        }
        if (erros === 0) {
          setImoveis([]);
          setSelecionados(new Set());
          setCalculo(null);
          setBuscaRealizada(false);
          setShowParametros(false);
        }
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao lançar reembolso';
        toast.error(msg);
      })
      .finally(() => setProcessando(false));
  };

  const handleLancar = (parametros) => {
    if (selecionados.size === 0) {
      toast.warn('Selecione ao menos um imóvel');
      return;
    }

    const codigosAtivo = imoveis
      .filter((i) => selecionados.has(i.codigo_boleto))
      .map((i) => i.codigo_ativo);

    const payload = buildPayload(parametros);

    verificarChavesReembolsoIptu({ codigos_ativo: codigosAtivo })
      .then((res) => {
        if (res.tem_chaves_nao_pagas) {
          const temPermissao = AuthStorage.temCaracteristica(CARACTERISTICA_REEMBOLSO_LIBERADO);
          if (!temPermissao) {
            toast.error('Existe(m) Ativo(s) vinculado(s) com chave(s) não paga(s). Você não tem permissão para continuar o lançamento.');
            return;
          }
          setChavesPendentes(res.chaves || []);
          setPayloadPendente(payload);
          setShowParametros(false);
          setShowConfirmacaoChaves(true);
        } else {
          executarLancamento(payload);
        }
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao verificar chaves';
        toast.error(msg);
      });
  };

  const handleConfirmarChaves = () => {
    setShowConfirmacaoChaves(false);
    if (payloadPendente) executarLancamento(payloadPendente);
  };

  const handleCancelarChaves = () => {
    setShowConfirmacaoChaves(false);
    setChavesPendentes([]);
    setPayloadPendente(null);
  };

  const handleCancelar = () => {
    setImoveis([]);
    setSelecionados(new Set());
    setCalculo(null);
    setFiltroAtual(null);
    setBuscaRealizada(false);
    setShowParametros(false);
  };

  return (
    <Container fluid>
      <Header title="Solicita Reembolso Clientes (IPTU)" />

      <FiltrosBusca
        onBuscar={handleBuscar}
        loading={loading}
        somenteLotesQuitados={somenteLotesQuitados}
        onChangeSomenteLotesQuitados={setSomenteLotesQuitados}
      />

      {calculo && !somenteLotesQuitados && <PainelCalculo calculo={calculo} />}

      {buscaRealizada && !loading && imoveis.length === 0 && (
        <Alert variant="info" className="mb-3">
          Nenhum imóvel encontrado para os filtros informados.
        </Alert>
      )}

      {imoveis.length > 0 && (
        <>
          <GridImoveis
            imoveis={imoveis}
            selecionados={selecionados}
            onToggle={handleToggle}
            onToggleTodos={handleToggleTodos}
            onEditarParcelas={handleEditarParcelas}
            onEditarValor={handleEditarValor}
            mostrarBotaoParametros
            onAbrirParametros={() => setShowParametros(true)}
          />

          <ParametrosLancamento
            show={showParametros}
            onClose={() => setShowParametros(false)}
            somenteLotesQuitados={somenteLotesQuitados}
            processando={processando}
            onLancar={handleLancar}
            onCancelar={() => setShowParametros(false)}
            onImportarExcel={() => {
              setShowParametros(false);
              setShowImportarExcel(true);
            }}
            temSelecionados={selecionados.size > 0}
          />
        </>
      )}

      <ModalImportarExcel
        show={showImportarExcel}
        onClose={() => setShowImportarExcel(false)}
        onAplicar={handleAplicarImportacao}
      />

      <ModalConfirmacaoChaves
        show={showConfirmacaoChaves}
        chaves={chavesPendentes}
        onConfirmar={handleConfirmarChaves}
        onCancelar={handleCancelarChaves}
      />
    </Container>
  );
};

export default ReembolsoIptu;
